import { useState } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Paper, Typography } from '@mui/material';
import { Megaphone } from '@/icons/iconComponents/Megaphone';
// Components
import { AlertBody, Toast } from '@/common/index';
import NoReferrals from '../NoReferrals/NoReferrals';
import ReferralTable from '../ReferralTable/ReferralTable';
// Hooks
import { useAuth } from '@/hooks/useAuth';
import { useWebiny } from '@/providers/WebinyProvider';
// Utils
import { colors } from '@/theme';
import { updateShareNamePref } from '@/resources/membership-service.resource';
// Styles
import styles from '../OfferCard/offer-card.module.css';

type Props = {
  hasTableData: boolean;
  shareName: boolean;
  showError: boolean;
  tableData: any;
};

export default function ReferralCard({
  hasTableData,
  shareName,
  showError,
  tableData,
}: Props): JSX.Element {
  const { referralTable } = useWebiny();
  const { memberData, fetcher, handleUpdateMemberData, updateFirebaseToken } = useAuth();
  const [isSharingName, setIsSharingName] = useState(memberData?.optInShareName);

  const message = shareName
    ? `${referralTable.hideNameText} `
    : `${referralTable.shareNameText} `;

  const tableComponent: JSX.Element = hasTableData ? (
    <ReferralTable tableData={tableData} cmsData={referralTable?.myReferrals} />
  ) : (
    <NoReferrals cmsData={referralTable?.noReferrals} />
  );

  const handleShareName = ({ target: { checked } }) => {
    setIsSharingName(checked);
    // call API to update share name preference
    fetcher(updateShareNamePref())
      .then((data) => {
        updateFirebaseToken(data);
        handleUpdateMemberData(data.token);
        Toast.success('Share name preference updated successfully.');
      })
      .catch(() => {
        Toast.error('Request to up preference failed. Please try again.');
      });
  };

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        marginBottom: '24px',
        marginTop: '24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '100px',
              backgroundColor: colors.primary[100],
            }}
          >
            <Megaphone className={styles.icon} />
          </Box>
        </Box>
        <Box>
          <Box>
            <Box sx={{ marginBottom: '16px' }}>
              <Typography variant="h3">{referralTable.title}</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignItems: 'flex-start',
              }}
            >
              <Typography variant="body2">{message}</Typography>
              <Box component="span" data-core-qa="ShareName_link">
                <FormControlLabel
                  label={referralTable.shareNameCheckboxLabel}
                  labelPlacement="end"
                  sx={{
                    '& .MuiFormControlLabel-la`bel': {
                      fontSize: '14px',
                    },
                  }}
                  control={
                    <Checkbox
                      data-core-qa="profileCheckboxShare"
                      id="optInShareNameValue"
                      size="small"
                      onChange={handleShareName}
                      checked={isSharingName || false}
                      sx={{ padding: '4px' }}
                    />
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {showError ? (
        <Alert variant="outlined" severity="error">
          <AlertBody
            header="System error."
            body="Referral data cannot be displayed at this time."
          />
        </Alert>
      ) : (
        tableComponent
      )}
    </Paper>
  );
}
